import { Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { Navigate } from 'react-router';

import { parse } from '@finley-technologies/valley-bank-utils';
import type { ValleyBankCustomerWithRelationships } from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankCustomerQuery } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { ProgressAlert } from '~/packages/common-frontend/components/Alert';
import { CollateralTable } from '~/valleyBank/domains/Collateral/ui/HoldingsTable';
import { useCustomerUrlParam } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { CustomerDetailsTemplate } from '~/valleyBank/domains/Customers/ui/details/CustomerDetailsTemplate';
import { CustomerNotFoundAlert } from '~/valleyBank/domains/Customers/ui/details/CustomerNotFoundAlert';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

// Fragments
const RouteBase = () => {
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const { cifnum } = useCustomerUrlParam();

  const [
    customer,
    setCustomer,
  ] = useState<ValleyBankCustomerWithRelationships | null>(null);

  const {
    loading: isDataLoading,
    error: isDataError,
  } = useGetValleyBankCustomerQuery({
    variables: {
      cifNumber: cifnum || '',
      reportingDate,
      isPreview,
    },
    onCompleted: (res) => {
      const customerJSON = res.valleyBankCustomer?.customer;
      if (customerJSON) {
        try {
          const newCustomer = parse.parseValleyBankCustomer(customerJSON);
          if (newCustomer) {
            setAsOfDate(newCustomer.asOfDate);
            setCustomer({ ...newCustomer });
          }
        } catch (err) {
          Sentry.captureException(err);
          throw new Error('Failed to parse customer');
        }
      }
    },
  });

  return (
    <>
      {isDataLoading && (
        <ProgressAlert status="info">
          <Text>{`Loading holdings for customer CIF Number [${cifnum}]`}</Text>
        </ProgressAlert>
      )}
      {!isDataLoading && !customer && <CustomerNotFoundAlert />}

      {customer && (
        <CollateralTable
          collateralAccounts={customer?.collateralAccounts || []}
          isDataLoading={isDataLoading}
          isDataError={Boolean(isDataError)}
          pageType="customers"
        />
      )}
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="./" />;
    }}
  >
    <CustomerDetailsTemplate selectedTab="holdings">
      <RouteBase />
    </CustomerDetailsTemplate>
  </RouteErrorBoundary>
);
